import type {
  PromptPreset,
  CreatePromptPresetInput,
  UpdatePromptPresetInput,
} from '@agentteams/runner-request/ui/lib/api';
export type {
  PromptPreset,
  CreatePromptPresetInput,
  UpdatePromptPresetInput,
} from '@agentteams/runner-request/ui/lib/api';
import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError, type PaginationMeta } from './common';

type PromptPresetResponse = {
  data: PromptPreset;
};

type PromptPresetListResponse = {
  data: PromptPreset[];
  meta: PaginationMeta;
};

const apiUrl = getApiUrl();

export const fetchPromptPresets = async (
  token: string,
  projectId: string,
  params?: { page?: number; pageSize?: number },
): Promise<PromptPresetListResponse> => {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();

  const response = await fetchWithAuthRetry(`${apiUrl}/api/projects/${projectId}/prompt-presets${qs ? `?${qs}` : ''}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch prompt presets: ${response.status}`);
  }

  return (await response.json()) as PromptPresetListResponse;
};

export const createPromptPreset = async (
  token: string,
  projectId: string,
  input: CreatePromptPresetInput,
): Promise<PromptPreset> => {
  const response = await fetchWithAuthRetry(`${apiUrl}/api/projects/${projectId}/prompt-presets`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create prompt preset: ${response.status}`);
  }

  const data = (await response.json()) as PromptPresetResponse;
  return data.data;
};

export const updatePromptPreset = async (
  token: string,
  projectId: string,
  id: string,
  input: UpdatePromptPresetInput,
): Promise<PromptPreset> => {
  const response = await fetchWithAuthRetry(`${apiUrl}/api/projects/${projectId}/prompt-presets/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update prompt preset: ${response.status}`);
  }

  const data = (await response.json()) as PromptPresetResponse;
  return data.data;
};

export const deletePromptPreset = async (token: string, projectId: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${apiUrl}/api/projects/${projectId}/prompt-presets/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete prompt preset: ${response.status}`);
  }
};
